import { Loader2, TriangleAlert } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';

export interface CancelPrescriptionDialogProps {
  open: boolean;
  prescriptionName: string;
  isPending: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

const TITLE_ID = 'cancel-prescription-dialog-title';
const DESCRIPTION_ID = 'cancel-prescription-dialog-description';

export function CancelPrescriptionDialog({
  open,
  prescriptionName,
  isPending,
  onConfirm,
  onOpenChange,
}: CancelPrescriptionDialogProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (isPending && !nextOpen) return;
    onOpenChange(nextOpen);
  };

  const handleKeep = () => {
    if (isPending) return;
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (isPending) return;
    onConfirm();
  };

  const target = prescriptionName.trim() === '' ? 'this prescription' : prescriptionName;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-labelledby={TITLE_ID}
        aria-describedby={DESCRIPTION_ID}
        showCloseButton={!isPending}
        className="border-red-500/60 bg-red-950/90 text-red-100 shadow-[0_0_40px_rgba(239,68,68,0.15)]"
      >
        <DialogHeader>
          <div className="flex items-start gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-red-500/20">
              <TriangleAlert className="size-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="space-y-1.5">
              <DialogTitle id={TITLE_ID} className="text-red-50">
                Cancel this prescription?
              </DialogTitle>
              <DialogDescription id={DESCRIPTION_ID} className="text-red-300/80">
                This will attempt to cancel{' '}
                <span className="font-medium text-red-50">{target}</span> before it reaches the
                pharmacy. If the prescription has already been picked up or filled, the cancellation
                may not succeed. This action cannot be undone.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={handleKeep}
            disabled={isPending}
            className="border-red-500/30 bg-red-900/60 text-red-200 hover:bg-red-800/60"
          >
            Keep prescription
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isPending}
            aria-busy={isPending}
            className="bg-red-600 text-white hover:bg-red-500"
          >
            {isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Cancelling…
              </>
            ) : (
              'Cancel prescription'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
